import { CalendarClock, PawPrint } from 'lucide-react';
import { useState } from 'react';

import {
  useDiscussoesControllerObterLinhaDoTempoDoHistorico,
  useDiscussoesControllerObterResumoDoHistorico,
} from '@/api/generated/discussões-técnicas/discussões-técnicas';
import { ResultadosDoExameDialogo } from '@/features/discussoes/components/ResultadosDoExameDialogo';
import { LinhaDoTempo } from '@/features/historico/components/LinhaDoTempo';
import { ESPECIES, formatarIdade } from '@/features/pacientes/lib/especies';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { Badge } from '@/shared/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Skeleton } from '@/shared/ui/skeleton';

interface Props {
  discussaoId: string;
  aberto: boolean;
  aoFechar: () => void;
}

/**
 * Histórico clínico do paciente da discussão — mesmo resumo + linha do tempo
 * que `HistoricoPage` mostra pro médico, liberado ao advisor por
 * PARTICIPAÇÃO na discussão (`DiscussoesController`), não por tenant.
 *
 * ⚠️ Só resumo e linha do tempo — sem as abas de exames/prontuário completas
 * nem o gráfico de evolução por analito. Essas dependem de rotas presas ao
 * tenant do médico (`/v1/exames`, `/v1/prontuarios`), que o advisor não tem;
 * só o que está aqui foi liberado por uma rota própria, escopada por
 * participação (`DiscussaoAcessoService.exigirParticipante`).
 *
 * ⚠️ Clicar num evento EXAME da linha do tempo abre o modal de resultados —
 * mas nem todo exame do histórico foi compartilhado com alguma discussão
 * deste participante (`ExamesService.obterCompartilhadoNaDiscussao` exige
 * isso). Pra quem não foi, o modal mostra erro em vez de vazar resultado —
 * é o 404 fazendo o trabalho dele, não um bug.
 */
export function HistoricoDoPacienteDialogo({ discussaoId, aberto, aoFechar }: Props) {
  const [exameId, setExameId] = useState<string | null>(null);

  const resumo = useDiscussoesControllerObterResumoDoHistorico(discussaoId, {
    query: { enabled: aberto },
  });
  const linha = useDiscussoesControllerObterLinhaDoTempoDoHistorico(
    discussaoId,
    { limite: 50 },
    { query: { enabled: aberto } },
  );

  return (
    <>
      <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Histórico do paciente</DialogTitle>
          </DialogHeader>

          {resumo.isPending ? (
            <div className="space-y-3">
              <Skeleton className="h-14 rounded-md" />
              <Skeleton className="h-40 rounded-md" />
            </div>
          ) : resumo.isError || !resumo.data ? (
            <EstadoErro aoTentarDeNovo={() => void resumo.refetch()} />
          ) : (
            <div className="space-y-5">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                    <PawPrint className="w-5 h-5 text-brand" aria-hidden="true" />
                    {resumo.data.pacienteNome}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {[
                      ESPECIES[resumo.data.especie as keyof typeof ESPECIES] ?? resumo.data.especie,
                      formatarIdade(resumo.data.idadeMeses),
                      resumo.data.tutorNome,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>

                <div className="flex flex-wrap gap-1.5">
                  <Badge variant="outline" className="font-normal">
                    {resumo.data.totalExames} exames
                  </Badge>
                  <Badge variant="outline" className="font-normal">
                    {resumo.data.totalProntuarios} prontuários
                  </Badge>
                  <Badge variant="outline" className="font-normal">
                    {resumo.data.totalReceituarios} receituários
                  </Badge>
                </div>
              </div>

              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
                  Linha do tempo
                </h4>

                {linha.isPending ? (
                  <div className="space-y-3">
                    {[0, 1, 2].map((i) => (
                      <Skeleton key={i} className="h-16 rounded-md" />
                    ))}
                  </div>
                ) : linha.isError || !linha.data ? (
                  <EstadoErro aoTentarDeNovo={() => void linha.refetch()} />
                ) : linha.data.itens.length === 0 ? (
                  <EstadoVazio
                    icone={CalendarClock}
                    titulo="Nada registrado ainda"
                    descricao="Exames, prontuários, requisições e receituários deste paciente aparecem aqui."
                  />
                ) : (
                  <LinhaDoTempo eventos={linha.data.itens} aoClicarExame={setExameId} />
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ResultadosDoExameDialogo exameId={exameId} aoFechar={() => setExameId(null)} />
    </>
  );
}
